import { isEmpty, omit } from 'lodash';
import {
  ACCOUNT_ID_IDENTIFIER,
  AUTO_GENERATE_ACCOUNT_ID,
  CUSTOM_DIVISION_CODE_MAPPING,
  DIRECT_DIVISION_CODE_MAPPING,
  DYNAMIC_ACCOUNT_ID,
  OPTIONAL_SECOND_ACCOUNT_ID,
  SECOND_ACCOUNT_ID,
  STATIC_ACCOUNT_ID,
} from '../../../../../store/constants/accountIdConfigTypes';
import { getSecondAccountIdConfigForSupplierRetailer } from '../../../../../services/accountIdService';
import { defaultValueForDirectDivisionCode } from './customAccountIdMappingHelper';

export class AccountIdFormValidator {
  constructor({ form, props }) {
    this.form = form;
    this.props = props;
    this.warehouseIdToNameMap = convertWarehousesToObject(props.warehouses);
    this.secondAccountIdConfig = getSecondAccountIdConfigForSupplierRetailer(props.accountIdTypes);
  }

  getFormValiditiy() {
    try {
      return this.validate();
    } catch (error) {
      console.warn('error getting form validity', error);
      return {
        isValid: true,
        reason: '',
      };
    }
  }

  validate() {
    if (this.form.type === STATIC_ACCOUNT_ID && this.secondAccountIdConfig.shouldShowSecondAccountIdInput) {
      return this.validateForStaticSecondAccountId({ isRequired: !this.secondAccountIdConfig.isOptional });
    }

    if (this.form.type === STATIC_ACCOUNT_ID) {
      return this.validateForStatic();
    }

    if (this.form.type === SECOND_ACCOUNT_ID) {
      return this.validateForStaticSecondAccountId({ isRequired: true });
    }

    if (this.form.type === OPTIONAL_SECOND_ACCOUNT_ID) {
      return this.validateForStaticSecondAccountId({ isRequired: false });
    }

    if (this.form.type === DYNAMIC_ACCOUNT_ID && this.form.identifier === ACCOUNT_ID_IDENTIFIER.WAREHOUSE) {
      return this.validateForDynamicWarehouse();
    }

    if (this.form.type === DYNAMIC_ACCOUNT_ID && this.form.identifier === ACCOUNT_ID_IDENTIFIER.SHIP_TO_COUNTRY) {
      return this.validateForDynamicShipToCountry();
    }

    return {
      isValid: true,
      reason: '',
    };
  }

  validateForStatic() {
    return {
      isValid: this.form.value.trim() !== '',
      reason: "There is not a value filled in for the 'Static Account id'",
    };
  }

  validateForStaticSecondAccountId({ isRequired = true } = {}) {
    if (isRequired) {
      return {
        isValid: this.form.value.trim() !== '' && this.form.second_account_id_value.trim() !== '',
        reason: 'A valid input is required for both the first and second account ids.',
      };
    }

    return {
      isValid: this.form.value.trim() !== '',
      reason: 'Please add a valid input for the first account id.',
    };
  }

  validateForDynamicWarehouse() {
    const validationErrors = [];
    Object.entries(this.warehouseIdToNameMap).forEach(([warehouseId, warehouseName]) => {
      const mappingEntry = this.form.mapping[warehouseId];

      if (!mappingEntry) {
        validationErrors.push(`Missing value for Warehouse: (${warehouseName})\n`);
        return; // Continue iteration even if mapping is missing
      }
      const { type, id, value = null } = mappingEntry;
      const isValidEntry = (function () {
        switch (type) {
          case SECOND_ACCOUNT_ID:
            return Array.isArray(id) && id.every((value) => value);
          case OPTIONAL_SECOND_ACCOUNT_ID:
            return Array.isArray(id) && id[0];
          case STATIC_ACCOUNT_ID:
          case AUTO_GENERATE_ACCOUNT_ID:
            return Array.isArray(id) ? id[0] : !!id;
          case CUSTOM_DIVISION_CODE_MAPPING:
            const invalidReasons = validateDynamicCustomMapping(value);
            validationErrors.push(...invalidReasons);

            return invalidReasons.length === 0;
          case DIRECT_DIVISION_CODE_MAPPING:
            return true;
          default:
            return false;
        }
      })();

      const hasCustomInvalidReason = type === CUSTOM_DIVISION_CODE_MAPPING;
      if (!isValidEntry) {
        validationErrors.unshift(
          `Invalid mapping for warehouse: (${warehouseName})\n- ${
            hasCustomInvalidReason ? '' : this.getInvalidReason(type, id)
          }`,
        );
      }
    });

    const isValid = !validationErrors.length;
    const reason = isValid ? '' : validationErrors.join('\n');
    return { isValid, reason };
  }

  validateForDynamicShipToCountry() {
    if (this.secondAccountIdConfig.shouldShowSecondAccountIdInput) {
      const isValid = Object.entries(this.form.mapping).every(([countryCode, ids]) => {
        if (this.secondAccountIdConfig.isOptional) {
          return ids.length > 0 && ids[0].trim() !== '';
        }
        return true; // make sure all values are present
      });
      return {
        isValid,
        reason: 'There are empty account ids missing from one or more countries.',
      };
    }

    if (isEmpty(this.form.mapping)) {
      return {
        isValid: false,
        reason: 'At least one country to account id setting is required.',
      };
    }

    return {
      isValid: Object.values(this.form.mapping).every((value) =>
        Array.isArray(value) ? !isEmpty(value[0]) : !isEmpty(value),
      ),
      reason: 'There are empty account ids missing from one or more country options',
    };
  }

  getInvalidReason(type, id) {
    switch (type) {
      case SECOND_ACCOUNT_ID:
        return 'Both values in the second account ID are required.';
      case OPTIONAL_SECOND_ACCOUNT_ID:
        return 'The first value for the optional second account ID is required.';
      case STATIC_ACCOUNT_ID:
        return 'A value is required for static type';
      case AUTO_GENERATE_ACCOUNT_ID:
        return 'A value is required in order for us to auto generate an account id.';
      default:
        return 'Invalid type.';
    }
  }

  get isMappingEmpty() {
    return isEmpty(this.form.mapping);
  }

  getValidityForSecondaryAccountId(arrayOfIds = []) {
    try {
      return arrayOfIds.every((ids) => {
        const hasAtLeastOneValidId = ids.some((id) => id.trim() !== '');
        const hasAllValidIds = ids.every((id) => id.trim() !== '');
        return this.secondAccountIdConfig.isOptional ? hasAtLeastOneValidId : hasAllValidIds;
      });
    } catch (error) {
      console.error('error validating second account id config', error);
      return true;
    }
  }
}

export function prepareFormValuesForUpdate({ form, props }) {
  const isStaticFormType = [STATIC_ACCOUNT_ID, OPTIONAL_SECOND_ACCOUNT_ID].includes(form.type);
  const secondAccountIdConfig = getSecondAccountIdConfigForSupplierRetailer(props.accountIdTypes);
  const hasSecondaryAccountIdConfig = Object.values(secondAccountIdConfig).some(Boolean);
  const shouldConvertStaticAccountIdValueToArray =
    isStaticFormType && hasSecondaryAccountIdConfig && form.second_account_id_value;

  if (shouldConvertStaticAccountIdValueToArray) {
    return {
      ...omit(form, 'second_account_id_value'),
      type: secondAccountIdConfig.isOptional ? OPTIONAL_SECOND_ACCOUNT_ID : SECOND_ACCOUNT_ID,
      value: [form.value, form.second_account_id_value],
      mapping: {},
    };
  }

  setDefaultValuesForDirectDivisionCodeMapping(form);

  return omit(form, 'second_account_id_value');
}

function convertWarehousesToObject(warehouses) {
  try {
    return warehouses.reduce((obj, { id, name }) => {
      obj[id] = name;
      return obj;
    }, {});
  } catch (error) {
    console.log('error creating warehouse object', error);
    return {};
  }
}

function validateDynamicCustomMapping(customMappingValue) {
  const invalidReasons = [];
  const { mappings = [] } = customMappingValue;
  const isEveryMappingValid = mappings.every((mapping) => mapping.key && mapping.value);

  if (!isEveryMappingValid) {
    const mappingIsInvalidReason = 'There is a missing key or value for the custom division code mapping';
    invalidReasons.push(mappingIsInvalidReason);
  }

  return invalidReasons;
}

function setDefaultValuesForDirectDivisionCodeMapping(form) {
  Object.entries(form.mapping).forEach(([warehouseId, value]) => {
    if (value.type === DIRECT_DIVISION_CODE_MAPPING) {
      form.mapping[warehouseId].value = defaultValueForDirectDivisionCode;
    }
  });
}

export const formInputFields = {
  radioOptions: {
    static: STATIC_ACCOUNT_ID,
    dynamic: DYNAMIC_ACCOUNT_ID,
  },
  dynamicIdentifierOptions: [
    {
      value: ACCOUNT_ID_IDENTIFIER.WAREHOUSE,
      label: 'Warehouse',
    },
    {
      value: ACCOUNT_ID_IDENTIFIER.SHIP_TO_COUNTRY,
      label: 'Ship to country',
    },
  ],
};

export const requiredCountryMapping = {
  '*': ['', ''],
};
export const defaultCountryMapping = {
  US: ['', ''],
};
export const requiredShipToCountryValues = Object.keys(requiredCountryMapping);

export const everyOtherCountryIsRequiredPopupHtml = (
  <p>
    In order for all orders to safely process, having an account id here is <b>required.</b>
  </p>
);
